"use client";

import { Monitor, Moon, Sun } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { useChangeLocale } from "@/app/hooks/useChangeLocale";
import { usePreferences } from "@/app/hooks/usePreferences";
import { useTheme } from "@/app/hooks/useTheme";
import { SettingsSection } from "@/components/settings/SettingsSection";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const LANGUAGES = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "zh-CN", name: "Chinese (Simplified)", nativeName: "简体中文" },
  { code: "zh-TW", name: "Chinese (Traditional)", nativeName: "繁體中文" },
] as const;

export function AppearanceSection() {
  const t = useTranslations("settings");
  const locale = useLocale();
  const { changeLocale, isPending } = useChangeLocale();
  const { theme, toggleTheme } = useTheme();
  const { updatePreferences } = usePreferences();
  const [mounted, setMounted] = useState(false);

  // Prevent hydration mismatch by only rendering theme-specific content after mount
  useEffect(() => {
    setMounted(true);
  }, []);

  const ThemeIcon = theme === "system" ? Monitor : theme === "light" ? Sun : Moon;
  const themeLabel = theme === "system" ? t("system") : theme === "light" ? t("light") : t("dark");
  const currentLanguage = LANGUAGES.find((lang) => lang.code === locale) || LANGUAGES[0];

  const handleLanguageChange = (newLocale: string) => {
    // Update user preference FIRST (immediate, no debounce for language changes)
    // This ensures the preference is saved before route change unmounts the component
    updatePreferences({ language: newLocale });

    // Then change URL locale for immediate UI update
    // This triggers a route transition which may unmount/remount components
    changeLocale(newLocale);
  };

  return (
    <SettingsSection>
      {/* Theme Setting */}
      <div className="flex flex-row items-center justify-between gap-4">
        <div>
          <h3 className="font-medium">{t("theme")}</h3>
        </div>
        <Button variant="outline" onClick={toggleTheme} className="gap-2">
          {mounted ? (
            <>
              <ThemeIcon className="w-4 h-4" />
              {themeLabel}
            </>
          ) : (
            <>
              <Monitor className="w-4 h-4" />
              {t("system")}
            </>
          )}
        </Button>
      </div>

      <div className="border-t border-border mt-4 pt-4"></div>

      {/* Language Setting */}
      <div className="flex flex-row items-center justify-between gap-4">
        <div>
          <h3 className="font-medium">{t("language")}</h3>
        </div>
        <Select
          value={mounted ? locale : "en"}
          onValueChange={handleLanguageChange}
          disabled={isPending || !mounted}
        >
          <SelectTrigger className="w-full sm:w-[280px]">
            <SelectValue>
              {mounted ? `${currentLanguage.nativeName} (${currentLanguage.name})` : t("loading")}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {LANGUAGES.map((lang) => (
              <SelectItem key={lang.code} value={lang.code}>
                {lang.nativeName} ({lang.name})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </SettingsSection>
  );
}
